import { effect, inject, Injectable, signal } from "@angular/core";

import { ActivatedRouteSnapshot, Router } from "@angular/router";

import { ScrollService } from "./scroll.service";

import { AppConfigService } from "src/app/core/appConfigs/app-config.service";

/**

 * Support audit-trail event types sent to Contact Us / CRM metadata.

 * Each value should describe a single, recognizable user or system moment.

 */

export type eventAction =
  // Route & view lifecycle

  | "route_entered" // User navigated to a route — clears support history
  | "view_ready" // Component finished initializing
  | "view_refreshed" // View reloaded data after an in-place user action

  // List & grid
  | "filter_applied" // User changed filters (before data load)
  | "filter_cleared" // User reset/cleared filters
  | "list_loaded" // Grid/list data loaded successfully
  | "page_changed" // User changed page or page size

  // Modals
  | "modal_opened"
  | "modal_ready"
  | "modal_confirmed" // User confirmed an action inside a modal
  | "modal_cancelled" // User dismissed/cancelled a modal
  | "modal_completed" // Modal closed after a successful workflow

  // Actions & API
  | "api_request_started" // User action triggered an API request (before response)
  | "action_succeeded" // User mutation completed (snooze, deny, save, delete…)
  | "action_failed" // User mutation failed
  | "data_fetch_failed" // Data load/resolver failed

  // Navigation & external
  | "navigated_to_detail"
  | "started_create_case"
  | "external_link_opened"

  // Forms
  | "form_submitted"
  | "form_submit_failed"

  // Reports & exports
  | "export_job_created"
  | "report_download_started"
  | "report_download_failed"
  | "websocket_job_update"

  // Search & UI
  | "search_performed"
  | "ui_state_changed";

export interface ViewContext<TViewModelState> {
  titleView: string;

  url: string;

  urlParams: { [key: string]: string };

  viewState: TViewModelState;

  eventAction: eventAction;

  eventDescription: string;

  /** Backend error message when eventAction is a failure type. */
  error?: string;

  scrollPosition: { scrollX: number; scrollY: number };

  //automaticallyScreenshot: File | null;

  timestamp: number;
}

/**

 * Append-only audit trail of user events per view (Contact Us / support CRM).

 * Each call to createContext() records what the user did (filter, pagination, navigation, etc.).

 *

 * NOT for restoring UI state on back-navigation — use RouteStore for that.

 */

@Injectable({
  providedIn: "root",
})
export class ViewContextHistoryStateService<TViewModelState> {
  constructor() {
    // effect(() => {
    //   if (this.contextHistory().length > 0) {
    //     console.clear();
    //
    //   }
    // });
  }

  private scrollService = inject(ScrollService);

  private contextHistory = signal<ViewContext<TViewModelState>[]>([]);

  private router = inject(Router);

  private appConfigService = inject(AppConfigService);

  getContextHistory() {
    console.log("getContextHistory", this.contextHistory());

    return this.contextHistory();
  }

  getCurrentPath(): string {
    const currentUrl =
      this.getContextHistory().length > 0
        ? this.getContextHistory()[this.getContextHistory().length - 1].url
        : this.router.url.split("?")[0].split("#")[0];

    return currentUrl;
  }

  /**

   * Records a support/CRM audit event (Contact Us flow).

   * NOT RouteStore — navigation state persistence uses RouteStore + route-view-state.helper.ts.

   */

  async createContext(context: {
    titleView: string;

    eventAction: eventAction;

    eventDescription: string;

    thisState: TViewModelState;

    /** Exact backend error message for failure events (action_failed, data_fetch_failed, etc.). */
    error?: string;

    setAutomaticallyScreenshot?: boolean;

    /** Keeps support context history when navigating to transient views (e.g. /error). */

    preserveHistory?: boolean;

    route: ActivatedRouteSnapshot;
  }): Promise<void> {
    const routeUrl =
      "/" +
      context.route.pathFromRoot

        .map((r) =>
          r.url

            .map((s) => s.path)

            .filter((p) => p)

            .join("/"),
        )

        .filter((p) => p)

        .join("/");

    const fullUrl = routeUrl;

    const currentUrl = fullUrl.split("?")[0].split("#")[0];

    if (!context.preserveHistory) {
      if (context.eventAction === "route_entered") {
        this.cleanContextHistory();
      } else if (
        this.contextHistory().length > 0 &&
        this.contextHistory()[this.contextHistory().length - 1].url !==
          currentUrl
      ) {
        this.cleanContextHistory();
      }
    }

    const now = Date.now();

    const paramString = fullUrl.split("?")[1] || "";

    const urlParams = paramString

      .split("#")[0]
      .split("&")

      .filter((s) => s)

      .reduce((acc: { [key: string]: string }, pair) => {
        const [key, value] = pair.split("=");

        if (key) {
          acc[decodeURIComponent(key)] = decodeURIComponent(value || "");
        }

        return acc;
      }, {});

    const shouldScreenshot = context.setAutomaticallyScreenshot ?? false;

    const newContext: ViewContext<TViewModelState> = {
      titleView: context.titleView,

      url: currentUrl,

      urlParams,

      viewState: context.thisState,

      eventAction: context.eventAction,

      eventDescription: context.eventDescription,

      ...(context.error ? { error: context.error } : {}),

      scrollPosition: this.scrollService.getScrollPosition(),

      // automaticallyScreenshot: shouldScreenshot

      //   ? await this.setAutomaticallyScreenshot()

      //   : null,

      timestamp: now,
    };

    this.contextHistory.update((current) => {
      const next = [...current, newContext];

      const maxItems =
        this.appConfigService.appConfigs()?.contactSupportConfigs
          .viewContextHistoryStateLimit!;

      if (next.length > maxItems) {
        return next.slice(next.length - maxItems);
      }

      return next;
    });
  }

  /**

   * Captures a screenshot of the current screen/window using getDisplayMedia.

   * Returns a File (image/png) ready to upload or store, or null if the user canceled.

   */

  private async setAutomaticallyScreenshot(): Promise<File | null> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      console.warn("Screen capture API not supported");

      return null;
    }

    let stream: MediaStream | null = null;

    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });

      const video = document.createElement("video");

      video.srcObject = stream;

      video.autoplay = true;

      video.muted = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          video

            .play()

            .then(() => resolve())

            .catch(reject);
        };

        video.onerror = reject;
      });

      const canvas = document.createElement("canvas");

      canvas.width = video.videoWidth;

      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");

      if (!context) {
        return null;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png"),
      );

      if (!blob) {
        return null;
      }

      return new File([blob], `screenshot-${Date.now()}.png`, {
        type: "image/png",
      });
    } catch (error) {
      console.error("Error capturing screenshot", error);

      return null;
    } finally {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    }
  }

  cleanContextHistory() {
    this.contextHistory.set([]);
  }
}
